import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import {
  AddressField,
  Customer,
  IInvoiceAttachmentRequest,
  IInvoiceServiceCharge,
  InvoiceReminder,
  InvoiceStatus,
  ParseInvoiceResponse
} from '../invoices.model';
import { ReminderConfig, ScheduleRelation } from 'components/reminder-modal/types';
import { ReminderApplyOn } from 'containers/settings/constants';
import { PredefinedOption } from 'components/day-picker/DayPicker';
import { InfoType } from './constants';
import { GetMerchantCustomizationsResponse } from 'containers/settings/settings.model';
import { InvoicesState } from '../InvoicesSlice';
import { DiscountFormData } from '../types';
import { DiscountV2, InvoicePayloadV2, ServiceFeeV2, TipV2 } from '../invoices.model';
import { isValidDiscountData } from '../utils';
import {
  getAttachmentArray,
  getLineItemsArrayForEvent,
  getServiceChargeEventPayload,
  prepareDiscountRelatedPayload,
  prepareLineItemList,
  prepareServiceChargeListRequestPayload,
  prepareSummatedUniqueTaxList
} from '../utils';
import { InvoiceSourceEnum, InvoiceVersion, TipTimingEnum } from '../constants';
import { IUniqueTaxRequestV2, InvoicePayload, LineItemV2, ServiceChargeV2 } from '../invoices.model';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';
import { constructArrayObjectAsString, parseToFixedTwoDecimals } from 'utils/commonUtils';
import { RelatedEntityData } from './types';
import { CustomerObject } from 'containers/customers/customers.model';
import { JobCustomer } from 'containers/jobs/jobs.model';
import { CustomerDetails } from '../invoices.model';

export const calcLineItemTotal = (unitCost: number | string = 0, quantity: number | string = 0) => {
  if (unitCost === '.') {
    return null;
  }
  return getAmountWithCurrency(+unitCost * +quantity, '$');
};

export const isValidNumber = (value: string): boolean => {
  return value !== '' && !isNaN(+value);
};

export const getInvoiceRemainderDaysText = ({
  status,
  createdAt,
  lastTrnxDate
}: {
  status: InvoiceStatus;
  createdAt: string;
  lastTrnxDate: string;
}) => {
  let statusDate: Date;
  if (status === InvoiceStatus.PENDING) {
    statusDate = new Date(createdAt);
    const days = DateUtils.getDifferenceInDays({ secondaryDate: statusDate });
    if (days === 0) {
      return 'Since today';
    }
    return `Since ${days} ${days > 1 ? 'Days' : 'Day'}`;
  }
  statusDate = new Date(lastTrnxDate);
  const days = DateUtils.getDifferenceInDays({ secondaryDate: statusDate });
  if (days === 0) {
    return 'Today';
  }
  return `${days} ${days > 1 ? 'Days' : 'Day'} ago`;
};

// Calculate default due date based on duration from settings
export const getDueDate = (referenceDate: Date, durationInDays: number): Date => {
  const calculatedDate = DateUtils.addDaysToDate(referenceDate, durationInDays);
  // Normalize to end of day
  return DateUtils.getEndOfDay(calculatedDate);
};

// Validate if reminders can be enabled
export const validateInvoiceReminderPrerequisites = (
  customer: Customer
): { canEnable: boolean; errorMessage: string; customerSectionMsg?: string; msgType: InfoType | null } => {
  // eslint-disable-next-line quotes
  const customerInfoMsg = "Want to send reminders? Add the customer's email and set up reminders below";
  if (!customer) {
    return {
      canEnable: false,
      errorMessage: 'Payment reminders cannot be sent due to missing customer.',
      msgType: InfoType.ERROR
    };
  } else if (!customer.email) {
    return {
      canEnable: false,
      errorMessage: 'Payment reminders cannot be sent due to missing customer email address.',
      // eslint-disable-next-line quotes
      customerSectionMsg: customerInfoMsg,
      msgType: InfoType.WARN
    };
  }
  return { canEnable: true, errorMessage: '', customerSectionMsg: '', msgType: null };
};

// Transform component reminders to API format for invoice create/edit
export const transformInvoiceRemindersToApiFormat = (reminders: ReminderConfig[]): InvoiceReminder[] => {
  return reminders.map((reminder, index) => ({
    ...(reminder?.reminderId ? { id: reminder?.reminderId } : {}),
    name: `Reminder ${index + 1}`,
    condition: {
      applyOn: ReminderApplyOn.DUE_DATE,
      timing: reminder.scheduleRelation,
      offset: reminder.days
    },
    remindOn: reminder.reminderDate || null,
    isSent: reminder.isSent || false
  }));
};

// Transform API reminders to component format
export const transformInvoiceApiRemindersToComponentFormat = (reminders: InvoiceReminder[]): ReminderConfig[] => {
  return reminders?.length > 0
    ? reminders.map((reminder, index) => ({
        id: `${index + 1}-${reminder.id}`,
        reminderNumber: index + 1,
        scheduleRelation: reminder.condition.timing as ScheduleRelation,
        days: reminder.condition.offset,
        reminderDate: reminder.remindOn || null,
        reminderId: reminder.id || null,
        isSent: reminder.isSent || false
      }))
    : [];
};

/**
 * Matcher function for date options (due dates, expiry dates, etc.)
 */
export const dateOptionMatcher = (option: PredefinedOption, selectedDate: Date): boolean => {
  // Custom option never matches based on date
  if (option.isCustom) {
    return false;
  }

  // Use the already calculated date from the option
  if (option.date) {
    return DateUtils.areSameDates(option.date, selectedDate);
  }

  return false;
};

// V1 Create Invoice Payload Function
// TODO: Remove this function once V2 released
export const getCreateInvoicePayload = (
  selectedInvoice: InvoicesState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  merchantCustomisations: GetMerchantCustomizationsResponse,
  selectedIsSignatureEnabled: boolean,
  invoiceReferenceNumber?: string,
  estimateId?: string,
  invoiceFromEstimateNumber?: string
): InvoicePayload => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayload(selectedInvoice.selectedServiceCharge);

  const invoicePayload: InvoicePayload = {
    version: InvoiceVersion.THREE_POINT_ZERO,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    // store all level amounts in redux slice eg: subtotal, finalamount etc
    amount: selectedInvoice.invoiceSubTotal.toString(),
    comment: selectedInvoice.comment,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    lineItems: prepareLineItemList(selectedInvoice.invoiceLineItems),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
    surchargePercentage: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
      : '0',
    surchargeAmount: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.valueInCurrencyWithBps?.toString() ?? '0'
      : '',
    isServiceFeeEnabled: selectedInvoice.isServiceFeeEnabled,
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachments),
    tipEnabled: selectedInvoice.tipEnabled,
    tncId: tncData.enabled ? tncData.tncId : 0,
    ...prepareSummatedUniqueTaxList({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItems,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : '0',
    source: InvoiceSourceEnum.WEB,
    isSignatureEnabled: selectedIsSignatureEnabled,
    serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? null,
    paymentModes: getPaymentModes(selectedInvoice.invoicePaymentModes, merchantCustomisations.paymentModes),
    ...(estimateId
      ? { relativeEntity: { entityType: 'ESTIMATE', entityId: estimateId, entityNumber: invoiceFromEstimateNumber } }
      : {})
  };

  const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
  logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceSave, {
    customerName: selectedInvoice?.invoiceCustomer?.name,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    amount: selectedInvoice.invoiceSubTotal.toString(),
    'Line Items': constructArrayObjectAsString(
      getLineItemsArrayForEvent(selectedInvoice.invoiceLineItems),
      'Line item'
    ),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
    surchargePercentage: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
      : '0',
    ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
    serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? ''
  });
  return invoicePayload;
};

// V2-Specific Prepare Functions for Nested Objects

export const prepareV2ServiceFeePayload = (invoiceState: InvoicesState): ServiceFeeV2 => {
  if (invoiceState.isServiceFeeEnabled) {
    return {
      enabled: invoiceState.isServiceFeeEnabled,
      type: 'PERCENTAGE',
      value: invoiceState.isServiceFeeEnabled ? +(invoiceState.currentSurcharge.percentage ?? '0') : 0,
      basePoints: invoiceState.isServiceFeeEnabled ? +(invoiceState.currentSurcharge.basePoints ?? '0') : 0,
      amount: invoiceState.isServiceFeeEnabled ? +(invoiceState.currentSurcharge.valueInCurrencyWithBps ?? 0) : 0
    };
  }
  return {
    enabled: false,
    type: 'PERCENTAGE',
    value: 0,
    basePoints: 0,
    amount: 0
  };
};

export const prepareV2TipPayload = (invoiceState: Pick<InvoicesState, 'tipEnabled' | 'tipTiming'>): TipV2 => {
  return {
    enabled: invoiceState.tipEnabled,
    ...(invoiceState.tipEnabled ? { type: invoiceState.tipTiming ?? TipTimingEnum.FINAL_PAYMENT_ONLY } : {})
  };
};

export const prepareV2DiscountPayload = (discountFormData: DiscountFormData): DiscountV2 => {
  if (isValidDiscountData(discountFormData)) {
    return {
      enabled: true,
      type: discountFormData.isDiscountAmountSelected ? 'ABSOLUTE' : 'PERCENTAGE',
      value: discountFormData.isDiscountAmountSelected
        ? +discountFormData.discountAmount
        : +discountFormData.discountPercent,
      amount: +discountFormData.discountInCurrency
    };
  }

  return {
    enabled: false,
    type: 'PERCENTAGE',
    value: 0,
    amount: 0
  };
};

// V2 Helper Functions using existing functions with .map() for type conversions

export const prepareLineItemListV2 = (invoiceLineItems: InvoicesState['invoiceLineItems']) => {
  const v1LineItems = prepareLineItemList(invoiceLineItems);
  return v1LineItems.map(item => ({
    ...item,
    qty: +item.qty,
    amount: +item.amount,
    unitPrice: +item.unitPrice,
    unitCost: item.unitCost ? +item.unitCost : undefined,
    markupPercentage: item.markupPercentage ? +item.markupPercentage : undefined,
    taxes: item.taxes?.map(tax => ({
      ...tax,
      percentage: +tax.percentage,
      amount: +tax.amount
    }))
  })) as LineItemV2[];
};

export const prepareSummatedUniqueTaxListV2 = (params: {
  appliedLineItemLevelTaxes: InvoicesState['appliedLineItemLevelTaxes'];
  invoiceLineItems: InvoicesState['invoiceLineItems'];
  selectedServiceCharge: InvoicesState['selectedServiceCharge'];
}) => {
  const v1TaxesResult = prepareSummatedUniqueTaxList(params);
  const v1Taxes = v1TaxesResult.taxes || [];
  return v1Taxes.map((tax: any) => ({
    catalogueTaxId: tax.catalogueTaxId,
    name: tax.name,
    percentage: +tax.percentage,
    amount: +tax.amount
  })) as IUniqueTaxRequestV2[];
};

export const prepareServiceChargeListRequestPayloadV2 = (serviceCharge: InvoicesState['selectedServiceCharge']) => {
  const v1ServiceCharges = prepareServiceChargeListRequestPayload(serviceCharge);
  return v1ServiceCharges.map(charge => ({
    ...charge,
    amount: +charge.amount,
    percentage: +charge.percentage,
    totalAmount: +charge.totalAmount,
    taxes: charge.taxes?.map(tax => ({
      ...tax,
      percentage: +tax.percentage,
      amount: +tax.amount
    }))
  })) as ServiceChargeV2[];
};

// V2 Create Invoice Payload Function
export const getCreateInvoiceV2Payload = (
  selectedInvoice: InvoicesState,
  currentTeam: { id: number; storeName: string },
  tncData: { enabled: boolean; tncId: number },
  merchantCustomisations: GetMerchantCustomizationsResponse,
  entityData?: RelatedEntityData
): InvoicePayloadV2 => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayloadV2(selectedInvoice.selectedServiceCharge);

  // Check if customer is from autofill without ID - pass entire customer object instead of just ID
  const hasCustomerId = selectedInvoice?.invoiceCustomer?.ID;
  const customerPayload = hasCustomerId
    ? {
        customerId: selectedInvoice.invoiceCustomer.ID,
        customerServiceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? undefined
      }
    : {
        customer: selectedInvoice?.invoiceCustomer
          ? {
              name: selectedInvoice.invoiceCustomer.name || '',
              lastName: selectedInvoice.invoiceCustomer.lastName || '',
              email: selectedInvoice.invoiceCustomer.email || '',
              phoneNumber: selectedInvoice.invoiceCustomer.phoneNumber || '',
              companyName: selectedInvoice.invoiceCustomer.companyName || '',
              referenceNumber: (selectedInvoice.invoiceCustomer as any).referenceNumber || '',
              note: (selectedInvoice.invoiceCustomer as any).note || '',
              serviceAddress: selectedInvoice.invoiceCustomer.serviceAddress || [],
              billingAddress: selectedInvoice.invoiceCustomer.billingAddress || null,
              isBillingSameAsService: (selectedInvoice.invoiceCustomer as any).isBillingSameAsService || false
            }
          : undefined
      };

  // Check if fields were modified after autofill
  const modifiedFields = selectedInvoice.autoFilledData
    ? getModifiedAutoFilledInvoiceFields(selectedInvoice.autoFilledData, selectedInvoice)
    : [];

  const invoicePayload: InvoicePayloadV2 = {
    ...customerPayload,
    invoiceDate: selectedInvoice.invoiceDate || new Date().toISOString(),
    invoiceDueDate: selectedInvoice.dueDate ?? undefined,
    lineItems: prepareLineItemListV2(selectedInvoice.invoiceLineItems),
    lineItemAmount: selectedInvoice.lineItemsTotal,
    discount: prepareV2DiscountPayload(selectedInvoice.discountFormData),
    discountTaxTiming:
      selectedInvoice.discountFormData?.discountTaxTiming ?? merchantCustomisations.discount?.taxTiming,
    subTotalAmount: selectedInvoice.invoiceSubTotal,
    serviceFee: prepareV2ServiceFeePayload(selectedInvoice),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : 0,
    taxes: prepareSummatedUniqueTaxListV2({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItems,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    totalAmount: selectedInvoice.finalAmount,
    note: selectedInvoice.comment || '',
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachments),
    paymentMethodList: getPaymentModes(selectedInvoice.invoicePaymentModes, merchantCustomisations.paymentModes),
    tip: prepareV2TipPayload(selectedInvoice),
    partialPaymentEnabled: selectedInvoice.partialPaymentEnabled,
    reminderEnabled: selectedInvoice.paymentRemindersEnabled,
    reminders: transformInvoiceRemindersToApiFormat(selectedInvoice.paymentReminders) ?? [],
    signatureEnabled: selectedInvoice.isSignatureEnabled,
    tncId: tncData.enabled ? tncData.tncId : 0,
    referenceId: selectedInvoice.invoiceReferenceNumber,
    storeName: currentTeam.storeName,
    version: InvoiceVersion.THREE_POINT_ONE,
    source: InvoiceSourceEnum.WEB,
    autoFillKey: selectedInvoice?.autoFillKey,
    ...(selectedInvoice?.autoFillKey ? { isAutofillModified: modifiedFields.length > 0 } : {})
  };

  // Add relative entity if creating from estimate or job
  if (entityData?.entityId && entityData?.entityType) {
    invoicePayload.relativeEntity = {
      entityType: entityData.entityType,
      entityId: entityData.entityId,
      entityNumber: entityData.entityNumber
    };
  }

  const serviceChargeEventPayload = getServiceChargeEventPayload(serviceChargeListPayload);
  logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceSave, {
    isPartialPaymentEnabled: selectedInvoice.partialPaymentEnabled,
    isReminderEnabled: selectedInvoice.paymentRemindersEnabled,
    reminderCount: selectedInvoice.paymentReminders.length,
    dueDate: selectedInvoice.dueDate ? DateUtils.parseISODateString(selectedInvoice.dueDate) : null,
    customerName: selectedInvoice?.invoiceCustomer?.name,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    teamId: currentTeam.id.toString(),
    storeName: currentTeam.storeName,
    amount: selectedInvoice.invoiceSubTotal.toString(),
    'Line Items': constructArrayObjectAsString(
      getLineItemsArrayForEvent(selectedInvoice.invoiceLineItems),
      'Line item'
    ),
    ...prepareDiscountRelatedPayload(selectedInvoice.discountFormData),
    surchargePercentage: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.percentageWithBps ?? '0'
      : '0',
    ...(serviceChargeEventPayload?.length > 0 ? { serviceCharges: serviceChargeEventPayload } : {}),
    serviceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? '',
    ...(selectedInvoice.autoFillKey
      ? {
          isAutoFilled: true,
          autofillFieldModified: modifiedFields.length > 0,
          autoFilledModifiedFields: modifiedFields?.join(', ')
        }
      : {})
  });

  const defaultReminderSettings = selectedInvoice.invoiceDefaultsFromSetting;
  if (defaultReminderSettings) {
    const remindersWereEdited = areRemindersEdited(
      selectedInvoice.paymentReminders,
      selectedInvoice.paymentRemindersEnabled,
      defaultReminderSettings.reminders,
      defaultReminderSettings.remindersEnabled
    );

    if (remindersWereEdited) {
      logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoiceRemindersEdited, {
        customerName: selectedInvoice?.invoiceCustomer?.name,
        customerId: selectedInvoice?.invoiceCustomer?.ID,
        teamId: currentTeam.id.toString(),
        storeName: currentTeam.storeName,
        remindersEnabled: selectedInvoice.paymentRemindersEnabled,
        remindersCount: selectedInvoice.paymentReminders.length,
        defaultRemindersEnabled: defaultReminderSettings.remindersEnabled,
        defaultRemindersCount: defaultReminderSettings.reminders.length
      });
    }
  }

  return invoicePayload;
};

// PDF Preview Payload for Create Invoice
export const getPDFPreviewPayload = (
  selectedInvoice: InvoicesState,
  currentTeam: { id: number; storeName: string },
  merchantCustomisations: GetMerchantCustomizationsResponse,
  tncData: { enabled: boolean; tncId: number },
  invoiceNumber: string
) => {
  const serviceChargeListPayload = prepareServiceChargeListRequestPayloadV2(selectedInvoice.selectedServiceCharge);

  return {
    invoiceNumber,
    customerId: selectedInvoice?.invoiceCustomer?.ID,
    customerServiceAddressId: selectedInvoice.invoiceCustomer?.serviceAddress?.[0]?.id ?? undefined,
    invoiceDate: selectedInvoice.invoiceDate || new Date().toISOString(),
    invoiceDueDate: selectedInvoice.dueDate ?? undefined,
    lineItems: prepareLineItemListV2(selectedInvoice.invoiceLineItems),
    lineItemAmount: selectedInvoice.lineItemsTotal,
    discount: prepareV2DiscountPayload(selectedInvoice.discountFormData),
    discountTaxTiming:
      selectedInvoice.discountFormData?.discountTaxTiming ?? merchantCustomisations.discount?.taxTiming,
    subTotalAmount: selectedInvoice.invoiceSubTotal,
    surchargeAmount: selectedInvoice.isServiceFeeEnabled
      ? selectedInvoice.currentSurcharge.valueInCurrencyWithBps?.toString() ?? '0'
      : '0',
    serviceFee: prepareV2ServiceFeePayload(selectedInvoice),
    serviceCharges: serviceChargeListPayload,
    serviceChargeAmount: serviceChargeListPayload?.length > 0 ? serviceChargeListPayload[0].totalAmount : 0,
    taxes: prepareSummatedUniqueTaxListV2({
      appliedLineItemLevelTaxes: selectedInvoice.appliedLineItemLevelTaxes,
      invoiceLineItems: selectedInvoice.invoiceLineItems,
      selectedServiceCharge: selectedInvoice.selectedServiceCharge
    }),
    totalAmount: selectedInvoice.finalAmount,
    note: selectedInvoice.comment || '',
    attachments: getAttachmentArray(selectedInvoice.selectedInvoiceAttachments),
    tip: prepareV2TipPayload(selectedInvoice),
    partialPaymentEnabled: selectedInvoice.partialPaymentEnabled,
    reminderEnabled: selectedInvoice.paymentRemindersEnabled,
    signatureEnabled: selectedInvoice.isSignatureEnabled,
    tncId: tncData.enabled ? tncData.tncId : 0,
    referenceId: selectedInvoice.invoiceReferenceNumber || '',
    storeName: currentTeam.storeName,
    version: InvoiceVersion.THREE_POINT_ONE,
    source: InvoiceSourceEnum.WEB
  };
};

// Payment modes utility function
export const getPaymentModes = (
  invoicePaymentModes: InvoicesState['invoicePaymentModes'],
  merchantPaymentModes: GetMerchantCustomizationsResponse['paymentModes']
) => {
  const selectedModes = invoicePaymentModes.filter(item => item.isSelected).map(item => item.id);

  const updatedPaymentModes = merchantPaymentModes.map(paymentMode => {
    if (paymentMode.teamLevelVisiblity) {
      if (selectedModes.includes(paymentMode.id)) {
        return {
          id: paymentMode.id,
          enabled: true
        };
      }
      return {
        id: paymentMode.id,
        enabled: false
      };
    }
    return {
      id: paymentMode.id,
      enabled: paymentMode.enabled
    };
  });
  return updatedPaymentModes;
};

const areRemindersEdited = (
  currentReminders: ReminderConfig[],
  currentEnabled: boolean,
  defaultReminders: ReminderConfig[],
  defaultEnabled: boolean
): boolean => {
  // Check if enabled status changed
  if (currentEnabled !== defaultEnabled) {
    return true;
  }

  // If reminders are disabled, no need to check further
  if (!currentEnabled) {
    return false;
  }

  if (currentReminders.length !== defaultReminders.length) {
    return true;
  }

  for (let i = 0; i < currentReminders.length; i++) {
    const current = currentReminders[i];
    const defaultReminder = defaultReminders[i];

    if (current.scheduleRelation !== defaultReminder?.scheduleRelation || current.days !== defaultReminder?.days) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if the selected customer matches the auto-filled customer data
 */
export const checkIsCustomerAutoFilled = (
  autoFilledCustomer: Customer | JobCustomer | null | undefined,
  selectedCustomer: CustomerObject | CustomerDetails | JobCustomer | Customer | null,
  getAddressAsString?: (address: any) => string
): boolean => {
  if (!autoFilledCustomer || !selectedCustomer) {
    return false;
  }

  // Check if each customer field matches exactly between autofilled and selected customer
  const isNameMatched = (autoFilledCustomer.name || '') === (selectedCustomer.name || '');
  const isLastNameMatched = (autoFilledCustomer.lastName || '') === (selectedCustomer.lastName || '');
  const isPhoneMatched = (autoFilledCustomer.phoneNumber || '') === (selectedCustomer.phoneNumber || '');
  const isEmailMatched = (autoFilledCustomer.email || '') === (selectedCustomer.email || '');
  const isCompanyMatched = (autoFilledCustomer.companyName || '') === (selectedCustomer.companyName || '');

  // Check billing address match
  let isBillingAddressMatched = true;
  if (getAddressAsString && (autoFilledCustomer.billingAddress || selectedCustomer.billingAddress)) {
    // Use string comparison for accuracy
    const autoFilledBillingStr = autoFilledCustomer.billingAddress
      ? getAddressAsString(autoFilledCustomer.billingAddress)
      : '';
    const selectedBillingStr = selectedCustomer.billingAddress
      ? getAddressAsString(selectedCustomer.billingAddress)
      : '';
    isBillingAddressMatched = autoFilledBillingStr.trim().toLowerCase() === selectedBillingStr.trim().toLowerCase();
  }

  return (
    isNameMatched &&
    isLastNameMatched &&
    isPhoneMatched &&
    isEmailMatched &&
    isCompanyMatched &&
    isBillingAddressMatched
  );
};

/**
 * Checks if a specific line item matches any auto-filled line item data
 * For invoice and job
 */
export const checkIsLineItemAutoFilled = (
  autoFilledLineItems: Array<{
    catalogueLineItemId?: number;
    name: string;
    unitPrice: number | string;
    qty: string | number;
    amount: string | number;
  }> | null,
  lineItem: {
    catalogueLineItemId?: number;
    itemDescription: string;
    unitCost: string | number;
    quantity: string | number;
    total: string | number;
  }
): boolean => {
  if (!autoFilledLineItems || autoFilledLineItems.length === 0) {
    return false;
  }

  return autoFilledLineItems.some(autoFilledLineItem => {
    const isCatalogueIdMatched = (autoFilledLineItem.catalogueLineItemId || 0) === (lineItem.catalogueLineItemId || 0);
    const isNameMatched = autoFilledLineItem.name && autoFilledLineItem.name === lineItem.itemDescription;
    const isUnitPriceMatched = autoFilledLineItem.unitPrice?.toString() === lineItem.unitCost?.toString();
    const isQuantityMatched =
      parseToFixedTwoDecimals(autoFilledLineItem.qty) === parseToFixedTwoDecimals(lineItem.quantity);
    const isTotalMatched =
      parseToFixedTwoDecimals(autoFilledLineItem.amount) === parseToFixedTwoDecimals(lineItem.total);

    return isCatalogueIdMatched && isNameMatched && isUnitPriceMatched && isQuantityMatched && isTotalMatched;
  });
};

/**
 * Same length and every current item matches autofilled (via checkIsLineItemAutoFilled). Section-level indicator.
 */
export const checkIsLineItemsSameAsAutoFilled = (
  currentLineItems:
    | Array<{
        catalogueLineItemId?: number;
        itemDescription: string;
        unitCost: string | number;
        quantity: string | number;
        total: number | string;
      }>
    | null
    | undefined,
  autoFilledLineItems:
    | Array<{
        catalogueLineItemId?: number;
        name: string;
        unitPrice: number | string;
        qty: string | number;
        amount: string | number;
      }>
    | null
    | undefined
): boolean => {
  const current = currentLineItems ?? [];
  const autoFilled = autoFilledLineItems ?? [];

  if (current.length !== autoFilled.length) {
    return false;
  }

  return current.every(lineItem => checkIsLineItemAutoFilled(autoFilled, lineItem));
};

/**
 * Checks if the current discount matches the autofilled discount
 */
export const checkIsDiscountAutoFilled = (
  autoFilledDiscount: { enabled: boolean; type: string; value: number; amount: number } | null | undefined,
  currentDiscount: DiscountFormData
): boolean => {
  if (!autoFilledDiscount?.enabled) {
    return false;
  }

  // Check if current discount is valid
  const hasDiscountValue = currentDiscount?.discountInCurrency && +currentDiscount.discountInCurrency > 0;
  if (!hasDiscountValue) {
    return false;
  }

  // Get autofilled value (percentage or amount based on type)
  const autoFilledValue = autoFilledDiscount.type === 'ABSOLUTE' ? autoFilledDiscount.amount : autoFilledDiscount.value;

  // Get current value (percentage or amount based on user selection)
  const currentValue = currentDiscount.isDiscountAmountSelected
    ? +currentDiscount.discountAmount
    : +currentDiscount.discountPercent;

  // Check if type and value match
  const isTypeMatched =
    (autoFilledDiscount.type === 'ABSOLUTE' && currentDiscount.isDiscountAmountSelected) ||
    (autoFilledDiscount.type === 'PERCENTAGE' && !currentDiscount.isDiscountAmountSelected);

  return isTypeMatched && autoFilledValue === currentValue;
};

/**
 * Checks if the current service charge matches the autofilled service charge.
 * For invoice, job, and estimate
 */
export const checkIsServiceChargeAutoFilled = (
  autoFilledServiceCharge:
    | { id: number; name: string; amount: string | number; percentage: string | number; totalAmount: string | number }
    | null
    | undefined,
  currentServiceCharge: IInvoiceServiceCharge
): boolean => {
  if (!autoFilledServiceCharge) {
    return false;
  }

  // Check if current service charge has valid data
  const hasValidServiceCharge = +currentServiceCharge.amount || +currentServiceCharge.percentage;
  if (!hasValidServiceCharge) {
    return false;
  }

  // Check if ID matches
  const isIdMatched = autoFilledServiceCharge.id === currentServiceCharge.id;

  // Check if name matches
  const isNameMatched = autoFilledServiceCharge.name === currentServiceCharge.name;

  // Check if amount matches
  const isAmountMatched =
    parseToFixedTwoDecimals(autoFilledServiceCharge.amount) === parseToFixedTwoDecimals(currentServiceCharge.amount);

  // Check if percentage matches
  const isPercentageMatched =
    parseToFixedTwoDecimals(autoFilledServiceCharge.percentage) ===
    parseToFixedTwoDecimals(currentServiceCharge.percentage);

  return isIdMatched && isNameMatched && isAmountMatched && isPercentageMatched;
};

/**
 * Checks if two service addresses match
 * For invoice and job
 */
export const checkIsServiceAddressAutoFilled = (
  autoFilledServiceAddress: AddressField | null | undefined,
  currentServiceAddress: AddressField | null | undefined,
  getAddressAsString: (address: any) => string
): boolean => {
  if (!autoFilledServiceAddress || !currentServiceAddress) {
    return false;
  }

  const autoFilledAddressString = getAddressAsString(autoFilledServiceAddress);
  const currentAddressString = getAddressAsString(currentServiceAddress);

  // Check if address strings match (ignoring case and trimming whitespace)
  return (
    autoFilledAddressString &&
    currentAddressString &&
    autoFilledAddressString.trim().toLowerCase() === currentAddressString.trim().toLowerCase()
  );
};

export const checkIsAttachmentsAutoFilled = (
  autoFilledAttachments: IInvoiceAttachmentRequest[] | null | undefined,
  currentAttachments: IInvoiceAttachmentRequest[] | null | undefined
): boolean => {
  if (!autoFilledAttachments || autoFilledAttachments.length === 0) {
    return false;
  }

  if (!currentAttachments || currentAttachments.length !== autoFilledAttachments.length) {
    return false;
  }

  return currentAttachments.every((attachment, index) => {
    return attachment.url === autoFilledAttachments[index]?.url;
  });
};

/**
 * Gets the list of modified fields by comparing autofilled data with current invoice state
 */
export const getModifiedAutoFilledInvoiceFields = (
  autoFilledData: ParseInvoiceResponse | null,
  currentInvoiceState: InvoicesState
): string[] => {
  if (!autoFilledData) return [];

  const modifiedFields: string[] = [];

  // Helper to get address as string for comparison
  const getAddressAsString = (address: AddressField) => {
    if (!address) return '';
    const { apartment, street, city, state, country, zipCode } = address;
    let addressString = '';
    if (street) addressString += ` ${street},`;
    if (apartment) addressString += ` ${apartment},`;
    if (city) addressString += ` ${city},`;
    if (state) addressString += ` ${state},`;
    if (country) addressString += ` ${country},`;
    if (zipCode) addressString += ` ${zipCode}`;
    return addressString.trim();
  };

  // Check customer data
  const isCustomerModified = !checkIsCustomerAutoFilled(
    autoFilledData.customer,
    currentInvoiceState.invoiceCustomer,
    getAddressAsString
  );
  if (autoFilledData.isAnyCustomerData && isCustomerModified) {
    modifiedFields.push('customer');
  }

  // Check service address
  const autoFilledServiceAddress = autoFilledData.customer?.serviceAddress?.[0];
  const currentServiceAddress = currentInvoiceState.invoiceCustomer?.serviceAddress?.[0];
  const isServiceAddressModified = !checkIsServiceAddressAutoFilled(
    autoFilledServiceAddress,
    currentServiceAddress,
    getAddressAsString
  );
  if (isServiceAddressModified && autoFilledServiceAddress) {
    modifiedFields.push('serviceAddress');
  }

  // Check billing address
  const autoFilledBillingAddress = autoFilledData.customer?.billingAddress;
  const currentBillingAddress = currentInvoiceState.invoiceCustomer?.billingAddress;
  if (autoFilledBillingAddress) {
    const autoFilledBillingStr = getAddressAsString(autoFilledBillingAddress);
    const currentBillingStr = currentBillingAddress ? getAddressAsString(currentBillingAddress) : '';
    if (autoFilledBillingStr && autoFilledBillingStr.trim().toLowerCase() !== currentBillingStr.trim().toLowerCase()) {
      modifiedFields.push('billingAddress');
    }
  }

  // Check due date
  if (autoFilledData.invoice?.invoiceDueDate && currentInvoiceState.dueDate) {
    const autoFilledDate = DateUtils.parseISODateString(autoFilledData.invoice.invoiceDueDate);
    const currentDate = DateUtils.parseISODateString(currentInvoiceState.dueDate);
    if (!DateUtils.areSameDates(autoFilledDate, currentDate)) {
      modifiedFields.push('dueDate');
    }
  }

  // Check reference number
  if (autoFilledData.invoice?.referenceId) {
    if (autoFilledData.invoice.referenceId.trim() !== currentInvoiceState.invoiceReferenceNumber.trim()) {
      modifiedFields.push('referenceNumber');
    }
  }

  // Check note/comment
  if (autoFilledData.invoice?.note) {
    if (autoFilledData.invoice.note.trim() !== currentInvoiceState.comment.trim()) {
      modifiedFields.push('note');
    }
  }

  // Check line items - same list as autofilled except order (1:1 correspondence)
  const autoFilledLineItems = autoFilledData.invoice?.lineItems || [];
  const currentLineItems = currentInvoiceState.invoiceLineItems || [];
  const isLineItemsUnchanged = checkIsLineItemsSameAsAutoFilled(currentLineItems, autoFilledLineItems);
  if (autoFilledData.invoice?.lineItems?.length > 0 && !isLineItemsUnchanged) {
    modifiedFields.push('lineItems');
  }

  // Check discount
  const autoFilledDiscount = autoFilledData.invoice?.discount;
  const currentDiscount = currentInvoiceState.discountFormData;
  const isDiscountModified = !checkIsDiscountAutoFilled(autoFilledDiscount, currentDiscount);
  if (autoFilledDiscount?.enabled && isDiscountModified) {
    modifiedFields.push('discount');
  }

  // Check service charges
  const autoFilledServiceCharge = autoFilledData.invoice?.serviceCharges?.[0];
  const currentServiceCharge = currentInvoiceState.selectedServiceCharge;
  if (autoFilledServiceCharge && currentServiceCharge) {
    const isServiceChargeModified =
      autoFilledServiceCharge && !checkIsServiceChargeAutoFilled(autoFilledServiceCharge, currentServiceCharge);
    if (isServiceChargeModified) {
      modifiedFields.push('serviceCharges');
    }
  }

  // Check attachments - use the shared utility function
  const autoFilledAttachments = autoFilledData.invoice?.attachments || [];
  const currentAttachments = currentInvoiceState.selectedInvoiceAttachments || [];
  const isAttachmentsModified =
    autoFilledAttachments?.length > 0 && !checkIsAttachmentsAutoFilled(autoFilledAttachments, currentAttachments);
  if (isAttachmentsModified) {
    modifiedFields.push('attachments');
  }

  return modifiedFields;
};
